
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { getCourse } from "@/lib/courses";
import { useCheckoutStore } from '@/stores/checkoutStore';
import CheckoutLayout from '@/components/checkout/new/CheckoutLayout';
import CustomerInfoSection from '@/components/checkout/new/CustomerInfoSection';
import CompanyInfoSection from '@/components/checkout/new/CompanyInfoSection';
import DiscountSection from '@/components/checkout/new/DiscountSection';
import PaymentSection from '@/components/checkout/new/PaymentSection';
import AuthRequiredModal from '@/components/checkout/new/AuthRequiredModal';

import { useEnrollmentRedirect } from './new-checkout/useEnrollmentRedirect';
import { Loader, CheckoutError } from './new-checkout/CheckoutErrorLoader';

const NewCheckout = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { initializeCheckout, error, recalculateDiscount, discountApplied } = useCheckoutStore();

  const [courseData, setCourseData] = React.useState<any>(null);
  const [courseLoading, setCourseLoading] = React.useState(true);
  const [courseError, setCourseError] = React.useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  const checkingEnrollment = useEnrollmentRedirect({
    user,
    courseId,
  });

  // Recalculate discount if already applied (when component mounts or course data changes)
  React.useEffect(() => {
    if (courseData && discountApplied) {
      recalculateDiscount();
    }
  }, [courseData, discountApplied, recalculateDiscount]);

  // Fetch course data and initialize checkout
  React.useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) {
        setCourseError("Kursust ei leitud");
        setCourseLoading(false);
        return;
      }

      try {
        setCourseLoading(true);
        const course = await getCourse(courseId);

        if (!course) {
          setCourseError("Kursust ei leitud");
        } else {
          setCourseData(course);
          initializeCheckout(course, {
            email: user?.email || '',
            phone: ''
          });
        }
      } catch (error) {
        setCourseError("Kursuse laadimine ebaõnnestus");
      } finally {
        setCourseLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, user, initializeCheckout]);

  // Handle authentication requirement
  React.useEffect(() => {
    if (!authLoading && !user && courseData) {
      setShowAuthModal(true);
    }
  }, [authLoading, user, courseData]);

  // Show error toast if there's an error
  React.useEffect(() => {
    if (error) {
      toast({
        title: "Viga",
        description: error,
        variant: "destructive",
      });
    }
  }, [error]);

  const handleAuthSuccess = () => setShowAuthModal(false);
  const handleAuthCancel = () => {
    // Navigate directly to courses page rather than course detail
    navigate('/courses');
  };

  if (courseLoading || checkingEnrollment) return <Loader loading />;
  if (courseError || !courseData) return <CheckoutError error={courseError} />;

  return (
    <>
      <CheckoutLayout course={courseData}>
        <CustomerInfoSection />
        <CompanyInfoSection />
        <DiscountSection />
        <PaymentSection courseId={courseId} />
      </CheckoutLayout>
      {showAuthModal && (
        <AuthRequiredModal 
          onSuccess={handleAuthSuccess} 
          onCancel={handleAuthCancel} 
        />
      )}
    </>
  );
};

export default NewCheckout;
